import React from "react";

import { GridColumn, GridItem, GridRow } from "commons/ui/Grid";
import InputText from "commons/ui/input/InputText";
import LineSeparator from "commons/ui/LineSeparator";

import ExtensionsProporsionInfo from "components/subComponents/ExtensionsProporsionInfo";
import TwoLanguagesAdornment from "components/subComponents/TwoLanguagesAdornment";
import AttachmentPicker from "components/ui/AttachmentPicker";
import TitleFunctionsTopBlock from "components/ui/TitleFunctionsTopBlock";

import useCheckSectionErrors from "hooks/form/blocks/useCheckSectionErrors";
import useSectionImageText from "hooks/form/blocks/useSectionImageText";
import useTitleFunctionsTopBlock from "hooks/useTitleFunctionsTopBlock";

import { BlockEnum, DesktopOrMobile } from "types/enums";

function SectionImageTextForm({ blockId }: { blockId: BlockEnum }) {
    const { buttonDelete, setMainImage, formHook } = useSectionImageText();

    useCheckSectionErrors({ formHook });

    const { checkErrorVisibility, setValue, form } = formHook;

    const titleFunctionsTopBlockHook = useTitleFunctionsTopBlock({
        buttonDelete: buttonDelete,
    });

    const desktopMobileText = React.useMemo(() => {
        if (blockId === BlockEnum.SectionOneImageTextCentered) {
            return {
                desktop: "1500x844px (max 400KB)",
                mobile: "1200x 675px (max 250KB)",
                proportion: "16:9",
            };
        }

        return {
            desktop: "2000x 629 px (max 400KB)",
            mobile: "1200x 377px (max 250KB)",
            proportion: undefined,
        };
    }, [blockId]);

    return (
        <>
            <TitleFunctionsTopBlock titleFunctionsTopBlockHook={titleFunctionsTopBlockHook} />

            {titleFunctionsTopBlockHook.closedBlock && (
                <>
                    <GridColumn>
                        <ExtensionsProporsionInfo
                            desktopText={desktopMobileText.desktop}
                            mobileText={desktopMobileText.mobile}
                            proportion={desktopMobileText.proportion}
                        />

                        <GridRow>
                            <GridItem>
                                <h3>Immagine</h3>
                            </GridItem>
                        </GridRow>

                        <GridRow>
                            <GridItem className={"col-xs-24-12"}>
                                <AttachmentPicker
                                    buttonLabel={"Foto Desktop"}
                                    setFile={(file) => setMainImage(file, DesktopOrMobile.desktop)}
                                    file={form.img.desktop}
                                    accept={".png,.jpg,.jpeg"}
                                    errors={checkErrorVisibility("img.desktop")}
                                />
                            </GridItem>

                            <GridItem className={"col-xs-24-12"}>
                                <AttachmentPicker
                                    buttonLabel={"Foto Mobile"}
                                    setFile={(file) => setMainImage(file, DesktopOrMobile.mobile)}
                                    file={form.img.mobile}
                                    accept={".png,.jpg,.jpeg"}
                                    errors={checkErrorVisibility("img.mobile")}
                                />
                            </GridItem>
                        </GridRow>
                    </GridColumn>

                    <LineSeparator />

                    <GridColumn>
                        <GridRow>
                            <GridItem className={"col-xs-12-12"}>
                                <h3>Testi</h3>
                            </GridItem>
                        </GridRow>

                        <GridRow>
                            <GridItem>
                                <span>Inserisci i testi per tutte le lingue</span>
                            </GridItem>
                        </GridRow>
                    </GridColumn>

                    <GridColumn>
                        <GridRow>
                            <div className={"col-xs-12-6"}>
                                <GridRow>
                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Titolo"}
                                            startAdornment={TwoLanguagesAdornment("it")}
                                            value={form.text.it.title}
                                            setValue={setValue}
                                            path={"text.it.title"}
                                            errors={checkErrorVisibility("text.it.title")}
                                            autoFocus
                                        />
                                    </GridItem>

                                    {blockId === BlockEnum.SectionOneImageTextCentered && (
                                        <GridItem className={"col-xs-12-12"}>
                                            <InputText
                                                label={"Sottotitolo"}
                                                startAdornment={TwoLanguagesAdornment("it")}
                                                value={form.text.it.subtitle}
                                                setValue={setValue}
                                                path={"text.it.subtitle"}
                                                errors={checkErrorVisibility("text.it.subtitle")}
                                            />
                                        </GridItem>
                                    )}

                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Testo nel bottone"}
                                            startAdornment={TwoLanguagesAdornment("it")}
                                            value={form.text.it.button_label}
                                            setValue={setValue}
                                            path={"text.it.button_label"}
                                            errors={checkErrorVisibility("text.it.button_label")}
                                        />
                                    </GridItem>

                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Url del bottone"}
                                            startAdornment={TwoLanguagesAdornment("it")}
                                            value={form.text.it.button_url}
                                            setValue={setValue}
                                            path={"text.it.button_url"}
                                            errors={checkErrorVisibility("text.it.button_url")}
                                        />
                                    </GridItem>

                                    {blockId !== BlockEnum.SectionOneImageTextCentered &&
                                        blockId !== BlockEnum.SectionOneImageTextFullBackground && (
                                            <GridItem className={"col-xs-12-12"}>
                                                <InputText
                                                    label={"Testo"}
                                                    startAdornment={TwoLanguagesAdornment("it")}
                                                    value={form.text.it.subtitle}
                                                    setValue={setValue}
                                                    path={"text.it.subtitle"}
                                                    errors={checkErrorVisibility(
                                                        "text.it.subtitle",
                                                    )}
                                                    textarea
                                                    largeBox
                                                />
                                            </GridItem>
                                        )}
                                </GridRow>
                            </div>

                            <div className={"col-xs-12-6"}>
                                <GridRow>
                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Titolo"}
                                            startAdornment={TwoLanguagesAdornment("en")}
                                            value={form.text.en.title}
                                            setValue={setValue}
                                            path={"text.en.title"}
                                            errors={checkErrorVisibility("text.en.title")}
                                        />
                                    </GridItem>

                                    {blockId === BlockEnum.SectionOneImageTextCentered && (
                                        <GridItem className={"col-xs-12-12"}>
                                            <InputText
                                                label={"Sottotolo"}
                                                startAdornment={TwoLanguagesAdornment("en")}
                                                value={form.text.en.subtitle}
                                                setValue={setValue}
                                                path={"text.en.subtitle"}
                                                errors={checkErrorVisibility("text.en.subtitle")}
                                            />
                                        </GridItem>
                                    )}

                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Testo nel bottone"}
                                            startAdornment={TwoLanguagesAdornment("en")}
                                            value={form.text.en.button_label}
                                            setValue={setValue}
                                            path={"text.en.button_label"}
                                            errors={checkErrorVisibility("text.en.button_label")}
                                        />
                                    </GridItem>

                                    <GridItem className={"col-xs-12-12"}>
                                        <InputText
                                            label={"Url del bottone"}
                                            startAdornment={TwoLanguagesAdornment("en")}
                                            value={form.text.en.button_url}
                                            setValue={setValue}
                                            path={"text.en.button_url"}
                                            errors={checkErrorVisibility("text.en.button_url")}
                                        />
                                    </GridItem>

                                    {blockId !== BlockEnum.SectionOneImageTextCentered &&
                                        blockId !== BlockEnum.SectionOneImageTextFullBackground && (
                                            <GridItem className={"col-xs-12-12"}>
                                                <InputText
                                                    label={"Testo"}
                                                    startAdornment={TwoLanguagesAdornment("en")}
                                                    value={form.text.en.subtitle}
                                                    setValue={setValue}
                                                    path={"text.en.subtitle"}
                                                    errors={checkErrorVisibility(
                                                        "text.en.subtitle",
                                                    )}
                                                    textarea
                                                    largeBox
                                                />
                                            </GridItem>
                                        )}
                                </GridRow>
                            </div>
                        </GridRow>
                    </GridColumn>
                </>
            )}
        </>
    );
}

export default SectionImageTextForm;
